import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNativeApp } from './platform';

export type BackAction = 'exit' | 'back' | 'home';

/**
 * Pure decision for the Android hardware back button.
 * - At the root route: leave the app.
 * - Elsewhere with history: step back.
 * - Elsewhere without history (e.g. opened via deep link): go home.
 */
export const resolveBackAction = (pathname: string, canGoBack: boolean): BackAction => {
  const normalized = (pathname || '/').replace(/\/+$/, '') || '/';
  if (normalized === '/') return 'exit';
  return canGoBack ? 'back' : 'home';
};

/** Wires the Android hardware back button. No-op on web. */
export const useNativeBackButton = (): void => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isNativeApp()) return;

    let remove: (() => void) | undefined;
    let cancelled = false;

    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('backButton', ({ canGoBack }) => {
          const action = resolveBackAction(window.location.pathname, canGoBack);
          if (action === 'exit') {
            void App.exitApp();
          } else if (action === 'back') {
            navigate(-1);
          } else {
            navigate('/', { replace: true });
          }
        });
        if (cancelled) {
          void handle.remove();
        } else {
          remove = () => void handle.remove();
        }
      } catch (error) {
        console.warn('[native] back button listener unavailable', error);
      }
    })();

    return () => {
      cancelled = true;
      remove?.();
    };
  }, [navigate, location.pathname]);
};
